import { useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { throwIfNotOk } from "@/lib/api-error";
import { Card, CardContent } from "@/components/ui/card";
import { AdminActionInbox, type AdminActionItem } from "@/components/admin/dashboard/AdminActionInbox";
import { AdminKpiBand } from "@/components/admin/dashboard/AdminKpiBand";
import { DateRangePicker, type DateRangeValue } from "@/components/admin/DateRangePicker";

export const Route = createFileRoute("/dashboard/")({
  component: DashboardIndex,
});

interface AdminStats {
  from: string; to: string;
  totalSessions: number; sessionsChange: number | null;
  completedSessions: number; completedChange: number | null;
  completionRate: number; totalRevenue: number; revenueChange: number | null;
  pendingPayouts: number; pendingPayoutSessionCount: number;
  newClientsInRange: number;
  actionItems: AdminActionItem[];
}

const km = (v: number) => `${v.toLocaleString("bs-BA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM`;

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultRange(): DateRangeValue {
  const today = formatLocalDate(new Date());
  return { from: today, to: today };
}

function DashboardIndex() {
  const { user } = useAuth();
  const role = user?.role as string | undefined;
  if (role === "admin") return <AdminOverview />;
  if (role === "supervisor") return <Navigate to="/dashboard/supervisor" />;
  if (role === "therapist") return <Navigate to="/dashboard/therapist" />;
  return <TherapistOverview />;
}

function AdminOverview() {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateRangeValue>(getDefaultRange);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stats", dateRange.from, dateRange.to],
    queryFn: async () => {
      const res = await fetch(`/api/admin/stats?from=${dateRange.from}&to=${dateRange.to}`);
      await throwIfNotOk(res, t("admin.dashboard.errorLoading"));
      return res.json() as Promise<AdminStats>;
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
        <div className="h-20 animate-pulse rounded-2xl border border-border/60 bg-card" />
        <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl border border-border/60 bg-card" />
          ))}
        </div>
        <div className="h-80 animate-pulse rounded-2xl border border-border/60 bg-card" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("admin.dashboard.title")}
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
            {t("admin.dashboard.subtitle")}
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
      </header>

      {/* KPI band */}
      <AdminKpiBand
        totalRevenue={data.totalRevenue}
        revenueChange={data.revenueChange}
        totalSessions={data.totalSessions}
        sessionsChange={data.sessionsChange}
        completedSessions={data.completedSessions}
        completedChange={data.completedChange}
        completionRate={data.completionRate}
        newClientsInRange={data.newClientsInRange}
        pendingPayouts={data.pendingPayouts}
        pendingPayoutSessionCount={data.pendingPayoutSessionCount}
        formatCurrency={km}
      />

      {/* Action center - what needs attention right now */}
      <AdminActionInbox items={data.actionItems} formatCurrency={km} />
    </div>
  );
}

function TherapistOverview() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-2xl border border-border/60 shadow-sm" style={{ background: "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))" }}>
        <div className="px-6 py-8 sm:px-8">
          <h1 className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "var(--font-heading)" }}>{t("therapist.dashboard.welcome")}</h1>
          <p className="mt-1 text-sm" style={{ color: "oklch(83.28% 0.0379 186.62)" }}>{t("therapist.dashboard.useMenu")}</p>
        </div>
      </div>
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t("therapist.dashboard.description")}
        </CardContent>
      </Card>
    </div>
  );
}
